import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiClient } from '../../apiClient';

const AdminBillingDetail = () => {
    const { invoiceId } = useParams();
    const [billing, setBilling] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [resending, setResending] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    useEffect(() => {
        const fetchDetail = async () => {
            try {
                const response = await apiClient.get(`/v1/admin/billing/${invoiceId}`);
                if (response.ok) {
                    const data = await response.json();
                    setBilling(data);
                } else {
                    setError("詳細データの取得に失敗しました。");
                }
            } catch (err) {
                console.error("Error fetching billing detail:", err);
                setError("通信エラーが発生しました。");
            } finally {
                setLoading(false);
            }
        };
        fetchDetail();
    }, [invoiceId]);

    const handleResend = async () => {
        setResending(true);
        try {
            const response = await apiClient.post(`/v1/admin/billing/${invoiceId}/resend`);
            if (response.ok) {
                alert("請求書を再送信しました。");
                setShowConfirm(false);
            } else {
                const data = await response.json();
                alert(`再送信に失敗しました: ${data.error || '不明なエラー'}`);
            }
        } catch (err) {
            alert("通信エラーが発生しました。");
        } finally {
            setResending(false);
        }
    };

    if (loading) return <div className="p-8">読み込み中...</div>;
    if (error) return <div className="p-8 text-red-600">{error}</div>;
    if (!billing) return <div className="p-8">データが見つかりません。</div>;

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('ja-JP');
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="mb-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold">請求詳細: {billing.companyName}</h2>
                <Link to="/admin/billing" className="text-indigo-600 hover:text-indigo-900">一覧に戻る</Link>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* 契約企業情報 */}
                <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4 border-b pb-2">契約企業情報</h3>
                    <dl className="grid grid-cols-1 gap-y-3">
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">BillWave顧客ID</dt>
                            <dd className="text-sm text-gray-900">{billing.accountId}</dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">会社名</dt>
                            <dd className="text-sm text-gray-900">{billing.companyName}</dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">契約プラン</dt>
                            <dd className="text-sm text-gray-900 uppercase font-bold">{billing.plan}</dd>
                        </div>
                    </dl>
                    <div className="mt-6 pt-4 border-t flex gap-4">
                        <Link to={`/admin/companies/${billing.accountId}`} className="text-sm text-indigo-600 hover:underline">
                            企業情報を開く
                        </Link>
                        {billing.stripeCustomerId && (
                             <a href={`https://dashboard.stripe.com/customers/${billing.stripeCustomerId}`} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline">
                                Stripe顧客を開く
                            </a>
                        )}
                    </div>
                </div>

                {/* 請求情報 */}
                <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4 border-b pb-2">請求情報</h3>
                    <dl className="grid grid-cols-1 gap-y-3">
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">Stripe Invoice ID</dt>
                            <dd className="text-sm text-gray-900 font-mono">{billing.invoiceId}</dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">請求対象月</dt>
                            <dd className="text-sm text-gray-900">{billing.billing_month}</dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">発行日</dt>
                            <dd className="text-sm text-gray-900">{formatDate(billing.processedAt)}</dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">支払期限</dt>
                            <dd className="text-sm text-gray-900">{formatDate(billing.billingPeriodEnd)}</dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">支払日時</dt>
                            <dd className="text-sm text-gray-900">{formatDate(billing.paid_at)}</dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">ステータス</dt>
                            <dd className="text-sm">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    billing.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                    {billing.status}
                                </span>
                            </dd>
                        </div>
                    </dl>
                    <div className="mt-6 pt-4 border-t">
                        <a href={`https://dashboard.stripe.com/invoices/${billing.invoiceId}`} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline">
                            Stripeで開く
                        </a>
                    </div>
                </div>
            </div>

            {/* 請求データ */}
            <div className="mt-8 bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 border-b pb-2">請求データ</h3>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                    <div className="flex justify-between">
                        <dt className="text-sm font-medium text-gray-500">対象期間</dt>
                        <dd className="text-sm text-gray-900">{formatDate(billing.billingPeriodStart)} ～ {formatDate(billing.billingPeriodEnd)}</dd>
                    </div>
                    <div className="flex justify-between">
                        <dt className="text-sm font-medium text-gray-500">請求書発行件数</dt>
                        <dd className="text-sm text-gray-900">{billing.invoice_count || 0} 件</dd>
                    </div>
                    <div className="flex justify-between">
                        <dt className="text-sm font-medium text-gray-500">基本料金上限件数</dt>
                        <dd className="text-sm text-gray-900">{billing.included_invoice_limit || 0} 件</dd>
                    </div>
                    <div className="flex justify-between">
                        <dt className="text-sm font-medium text-gray-500 text-red-600 font-bold">超過発行件数</dt>
                        <dd className="text-sm text-red-600 font-bold">{billing.overageForBilling || 0} 件</dd>
                    </div>
                </dl>
            </div>

            {/* 請求明細 */}
            <div className="mt-8 bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 border-b pb-2">請求明細</h3>
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">項目</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">金額</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        <tr>
                            <td className="px-6 py-4 text-sm text-gray-900">基本料金 ({billing.plan_type})</td>
                            <td className="px-6 py-4 text-sm text-gray-900 text-right">¥{billing.base_price?.toLocaleString()}</td>
                        </tr>
                        <tr>
                            <td className="px-6 py-4 text-sm text-gray-900">従量課金 (超過 {billing.overageForBilling}件 × ¥{billing.overage_unit_price})</td>
                            <td className="px-6 py-4 text-sm text-gray-900 text-right">¥{billing.overage_amount?.toLocaleString()}</td>
                        </tr>
                        
                        {/* 調整明細の表示 */}
                        {billing.adjustments && billing.adjustments.map((adj, idx) => (
                            <tr key={idx} className="text-red-600 bg-red-50/30">
                                <td className="px-6 py-4 text-sm font-medium">{adj.description}</td>
                                <td className="px-6 py-4 text-sm text-right font-bold">-¥{adj.amountEx?.toLocaleString()}</td>
                            </tr>
                        ))}

                        <tr>
                            <td className="px-6 py-4 text-sm text-gray-500 border-t">小計</td>
                            <td className="px-6 py-4 text-sm text-gray-500 text-right border-t">¥{billing.subtotal?.toLocaleString()}</td>
                        </tr>
                        <tr>
                            <td className="px-6 py-4 text-sm text-gray-500">消費税</td>
                            <td className="px-6 py-4 text-sm text-gray-500 text-right">¥{billing.tax?.toLocaleString()}</td>
                        </tr>
                        <tr className="bg-gray-50 font-bold">
                            <td className="px-6 py-4 text-sm text-gray-900 text-lg">合計金額</td>
                            <td className="px-6 py-4 text-sm text-indigo-600 text-right text-lg">¥{billing.total?.toLocaleString()}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* 操作・関連情報 */}
            <div className="mt-8 bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 border-b pb-2">操作</h3>
                <div className="flex flex-wrap gap-4">
                    {billing.status === 'open' && (
                        <button
                            onClick={() => setShowConfirm(true)}
                            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
                        >
                            請求書を再送信する
                        </button>
                    )}
                    {billing.stripeInvoicePdfUrl && (
                        <a
                            href={billing.stripeInvoicePdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-white border px-4 py-2 rounded hover:bg-gray-100"
                        >
                            請求書PDFを開く
                        </a>
                    )}
                </div>
            </div>

            {/* 確認モーダル */}
            {showConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full">
                        <h3 className="text-lg font-bold mb-4">請求書の再送信</h3>
                        <p className="text-sm text-gray-600 mb-6">
                            この請求書メールを顧客に再送信しますか？
                        </p>
                        <div className="flex gap-4">
                            <button
                                onClick={() => setShowConfirm(false)}
                                className="flex-1 px-4 py-2 border rounded text-gray-700 hover:bg-gray-50"
                            >
                                キャンセル
                            </button>
                            <button
                                onClick={handleResend}
                                disabled={resending}
                                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {resending ? '送信中...' : '再送信する'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminBillingDetail;
